import React, { useState, useEffect } from 'react';
import { Dog } from '../types';
import { X, Calendar, Heart, Syringe, Skull, FileText, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

interface DogDetailProps {
  dog: Dog;
  onClose: () => void;
}

export const DogDetail: React.FC<DogDetailProps> = ({ dog, onClose }) => {
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);
  const photos = dog.photos || [];
  const hasPhotos = photos.length > 0;
  const isDead = !!dog.deathDate;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (fullscreenIndex === null) return;

      if (e.key === 'Escape') {
        setFullscreenIndex(null);
      } else if (e.key === 'ArrowRight') {
        setFullscreenIndex((prev) => (prev !== null && prev < photos.length - 1 ? prev + 1 : 0));
      } else if (e.key === 'ArrowLeft') {
        setFullscreenIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : photos.length - 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenIndex, photos]);

  const handleNextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFullscreenIndex((prev) => (prev !== null && prev < photos.length - 1 ? prev + 1 : 0));
  };

  const handlePrevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFullscreenIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : photos.length - 1));
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-[fadeIn_0.2s_ease-out]">
        <div className="w-full max-w-4xl bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
          
          {/* Header / Close */}
          <div className="absolute top-4 right-4 z-20">
            <button 
              onClick={onClose} 
              className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors backdrop-blur-md"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="flex flex-col md:flex-row h-full overflow-hidden">
            {/* Image Carousel */}
            <div className="w-full md:w-1/2 bg-gray-900 relative flex flex-col justify-center max-h-[50vh] md:max-h-full group">
              {hasPhotos ? (
                <>
                  <div className="flex overflow-x-auto snap-x snap-mandatory h-full w-full no-scrollbar">
                    {photos.map((photo, index) => (
                      <div 
                        key={index} 
                        className="flex-shrink-0 w-full h-full snap-center flex items-center justify-center bg-black cursor-zoom-in relative"
                        onClick={() => setFullscreenIndex(index)}
                      >
                        <img 
                          src={photo} 
                          alt={`${dog.name} ${index + 1}`} 
                          className={`max-h-full max-w-full object-contain ${isDead ? 'grayscale opacity-90' : ''}`} 
                        />
                        <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                             <ZoomIn className="text-white/80 h-12 w-12 drop-shadow-lg" />
                        </div>
                      </div>
                    ))}
                  </div>
                   <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 pointer-events-none">
                     <span className="text-xs text-white/70 bg-black/30 px-2 py-1 rounded-full backdrop-blur-sm">
                        Hacer clic para ampliar
                     </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No hay foto disponible
                </div>
              )}
              
              {hasPhotos && photos.length > 1 && (
                <div className="absolute bottom-12 left-0 right-0 flex justify-center gap-2 pointer-events-none">
                  {photos.map((_, i) => (
                    <div key={i} className="w-2 h-2 rounded-full bg-white/50 shadow" />
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="w-full md:w-1/2 p-8 overflow-y-auto bg-white">
              <div className="mb-6">
                <div className="flex justify-between items-start">
                   <div>
                      <h2 className="text-4xl font-black bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600 bg-clip-text text-transparent mb-2">
                          {dog.name}
                      </h2>
                      <div className="flex gap-2 mb-2">
                           {dog.gender === 'male' ? 
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">Masculino</span> : 
                              <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-bold">Femenino</span>
                          }
                          <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">T{dog.terrain}</span>
                          {isDead && (
                              <span className="px-3 py-1 bg-black text-white rounded-full text-sm font-bold flex items-center gap-1">
                                  <Skull className="h-4 w-4" /> Fallecido
                              </span>
                          )}
                      </div>
                   </div>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-4">
                   {dog.chip ? 
                       <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 border border-green-200 text-sm font-medium">Chipado</span> :
                       <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200 text-sm font-medium">No chipado</span>
                   }
                   {dog.isNeutered ?
                       <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 border border-orange-200 text-sm font-medium">Castrado</span> :
                       <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200 text-sm font-medium">No castrado</span>
                   }
                </div>
              </div>

              <div className="space-y-6">
                  <div className="flex items-start gap-3 p-4 bg-rose-50 rounded-xl border border-rose-100">
                      <Heart className="h-6 w-6 text-rose-500 mt-1 flex-shrink-0" />
                      <div>
                          <p className="text-sm text-rose-400 font-bold uppercase tracking-wider">Padrino</p>
                          <p className="text-lg text-rose-900 font-semibold">{dog.godparent || 'Aún no se ha encontrado padrino'}</p>
                      </div>
                  </div>

                  {dog.notes && (
                    <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                        <FileText className="h-6 w-6 text-yellow-500 mt-1 flex-shrink-0" />
                        <div>
                            <p className="text-sm text-yellow-500 font-bold uppercase tracking-wider">Notas</p>
                            <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{dog.notes}</p>
                        </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                          <div className="flex items-center gap-2 mb-1">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <p className="text-xs text-gray-500 font-bold uppercase">Ingreso</p>
                          </div>
                          <p className="text-gray-800 font-medium">{new Date(dog.admissionDate).toLocaleDateString('es-ES')}</p>
                      </div>
                      {dog.birthDate && (
                          <div className="p-4 bg-orange-50 rounded-xl border border-orange-100">
                              <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs text-orange-600 font-bold uppercase">Nacimiento</span>
                              </div>
                              <p className="text-orange-800 font-medium">{new Date(dog.birthDate).toLocaleDateString('es-ES')}</p>
                          </div>
                      )}
                      {isDead && (
                          <div className="p-4 bg-gray-900 rounded-xl border border-gray-700">
                              <div className="flex items-center gap-2 mb-1">
                                  <Skull className="h-4 w-4 text-gray-400" />
                                  <p className="text-xs text-gray-400 font-bold uppercase">Fallecido</p>
                              </div>
                              <p className="text-white font-medium">{new Date(dog.deathDate!).toLocaleDateString('es-ES')}</p>
                          </div>
                      )}
                      {dog.chip && (
                          <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                              <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs text-green-600 font-bold uppercase">Chip</span>
                              </div>
                              <p className="text-green-800 font-medium text-sm">SN: {dog.chip.serialNumber}</p>
                              <p className="text-green-700 text-sm">{dog.chip.location}</p>
                          </div>
                      )}
                      {dog.passportNumber && (
                          <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                              <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs text-blue-600 font-bold uppercase">Pasaporte</span>
                              </div>
                              <p className="text-blue-800 font-medium">{dog.passportNumber}</p>
                          </div>
                      )}
                  </div>

                  <div>
                      <div className="flex items-center gap-2 mb-3">
                          <Syringe className="h-5 w-5 text-teal-500" />
                          <h3 className="text-lg font-bold text-gray-800">Vacunas</h3>
                      </div>
                      {dog.vaccinations && dog.vaccinations.length > 0 ? (
                          <div className="space-y-2">
                              {dog.vaccinations.map(vac => (
                                  <div key={vac.id} className="flex justify-between items-center p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                                      <span className="font-medium text-gray-700">{vac.name}</span>
                                      <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">{new Date(vac.date).toLocaleDateString('de-DE')}</span>
                                  </div>
                              ))}
                          </div>
                      ) : (
                          <p className="text-gray-400 italic">No hay vacunas registradas.</p>
                      )}
                  </div>

                  {dog.nextVaccination && (
                      <div>
                          <div className="flex items-center gap-2 mb-3">
                              <Calendar className="h-5 w-5 text-blue-500" />
                              <h3 className="text-lg font-bold text-gray-800">Próxima vacuna programada</h3>
                          </div>
                          <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                              <div className="flex items-center justify-between">
                                  <div>
                                      <p className="font-medium text-gray-800">{dog.nextVaccination.name}</p>
                                      <p className="text-sm text-gray-600">Geplant für {new Date(dog.nextVaccination.date).toLocaleDateString('de-DE')}</p>
                                  </div>
                                  <div className="text-right">
                                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                          Ausstehend
                                      </span>
                                  </div>
                              </div>
                          </div>
                      </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Lightbox */}
      {fullscreenIndex !== null && hasPhotos && (
        <div className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center animate-[fadeIn_0.2s_ease-out]">
            {/* Controls */}
            <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-center z-50 bg-gradient-to-b from-black/50 to-transparent">
               <span className="text-white/80 font-medium">
                  {fullscreenIndex + 1} / {photos.length}
               </span>
               <button 
                  onClick={() => setFullscreenIndex(null)}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                >
                  <X className="h-8 w-8" />
               </button>
            </div>

            {/* Main Image */}
            <div className="w-full h-full p-4 md:p-10 flex items-center justify-center" onClick={() => setFullscreenIndex(null)}>
                <img 
                   src={photos[fullscreenIndex]} 
                   alt={dog.name} 
                   className={`max-w-full max-h-full object-contain drop-shadow-2xl transition-transform duration-300 ${isDead ? 'grayscale' : ''}`}
                   onClick={(e) => e.stopPropagation()}
                />
            </div>

            {/* Navigation Arrows */}
            {photos.length > 1 && (
              <>
                <button 
                  onClick={handlePrevPhoto}
                  className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-all hover:scale-110"
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
                <button 
                  onClick={handleNextPhoto}
                  className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-all hover:scale-110"
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
              </>
            )}

            {/* Thumbnails Strip at bottom */}
            <div className="absolute bottom-4 left-0 right-0 overflow-x-auto flex justify-center gap-2 px-4 py-2 no-scrollbar">
                {photos.map((p, idx) => (
                    <button 
                        key={idx}
                        onClick={(e) => { e.stopPropagation(); setFullscreenIndex(idx); }}
                        className={`h-16 w-16 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${idx === fullscreenIndex ? 'border-white scale-110' : 'border-transparent opacity-50 hover:opacity-100'}`}
                    >
                        <img src={p} className={`w-full h-full object-cover ${isDead ? 'grayscale' : ''}`} />
                    </button>
                ))}
            </div>
        </div>
      )}
    </>
  );
};
